import { useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { clampWidth, defaultWidths, parseWidths, serializeWidths, widthsStorageKey, type TableId, type WidthSpec } from "./tableState.ts";

export type DataColumn<Row> = {
  key: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  sortable?: boolean;
  rowHeader?: boolean;
  className?: string;
  filter?: ReactNode;
  cell: (row: Row) => ReactNode;
};

export type TableSort = { key: string; direction: "asc" | "desc" };

type Drag = { key: string; startX: number; start: number };

const RESIZE_STEP = 16;

function readWidths(tableId: TableId | undefined, columns: readonly WidthSpec[]): Map<string, number> {
  if (tableId === undefined) return defaultWidths(columns);
  try {
    return parseWidths(localStorage.getItem(widthsStorageKey(tableId)), columns);
  } catch {
    return defaultWidths(columns);
  }
}

function storeWidths(tableId: TableId | undefined, widths: ReadonlyMap<string, number>): void {
  if (tableId === undefined) return;
  try {
    localStorage.setItem(widthsStorageKey(tableId), serializeWidths(widths));
  } catch {
    /* storage is disabled; widths stay in memory */
  }
}

export function DataTable<Row>(props: {
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  columns: readonly DataColumn<Row>[];
  className?: string;
  sort: TableSort | null;
  onSort: (key: string) => void;
  tableId?: TableId;
  rowClassName?: (row: Row) => string | undefined;
}) {
  const [widths, setWidths] = useState<Map<string, number>>(() => readWidths(props.tableId, props.columns));
  const [drag, setDrag] = useState<Drag | null>(null);

  function widthOf(key: string): number {
    return widths.get(key) ?? props.columns.find((column) => column.key === key)?.defaultWidth ?? 72;
  }

  function share(key: string): string {
    const total = props.columns.reduce((sum, column) => sum + widthOf(column.key), 0);
    return `${((widthOf(key) / total) * 100).toFixed(2)}%`;
  }

  function resized(column: DataColumn<Row>, width: number): Map<string, number> {
    const next = new Map(widths);
    next.set(column.key, clampWidth(column, width));
    setWidths(next);
    return next;
  }

  function startResize(key: string, event: ReactPointerEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    const header = event.currentTarget.closest("th");
    const measured = header?.getBoundingClientRect().width ?? 0;
    const start = widths.get(key) ?? (measured > 0 ? measured : 120);
    if (typeof event.currentTarget.setPointerCapture === "function") {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* jsdom has no capture */
      }
    }
    setDrag({ key, startX: event.clientX, start });
  }

  function moveResize(event: ReactPointerEvent<HTMLButtonElement>) {
    const point = Number.isFinite(event.clientX) ? event.clientX : event.nativeEvent.clientX;
    if (drag === null || !Number.isFinite(point)) return;
    const column = props.columns.find((item) => item.key === drag.key);
    if (column) resized(column, drag.start + point - drag.startX);
  }

  function endResize(event: ReactPointerEvent<HTMLButtonElement>) {
    if (typeof event.currentTarget.releasePointerCapture === "function" && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* already released */
      }
    }
    if (drag !== null) storeWidths(props.tableId, widths);
    setDrag(null);
  }

  function stepResize(column: DataColumn<Row>, event: ReactKeyboardEvent<HTMLButtonElement>) {
    const step = event.key === "ArrowRight" ? RESIZE_STEP : event.key === "ArrowLeft" ? -RESIZE_STEP : 0;
    if (step === 0) return;
    event.preventDefault();
    storeWidths(props.tableId, resized(column, widthOf(column.key) + step));
  }

  return (
    <table className={props.className ?? "data-table"}>
      <colgroup>
        {props.columns.map((column) => (
          <col key={column.key} style={{ width: share(column.key) }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {props.columns.map((column) => {
            const sorted = column.sortable && props.sort?.key === column.key ? props.sort.direction : null;
            const ariaSort = column.sortable ? (sorted === null ? "none" : sorted === "asc" ? "ascending" : "descending") : undefined;
            return (
              <th key={column.key} scope="col" className={column.className} style={{ width: share(column.key) }} aria-sort={ariaSort}>
                <span className="column-head">
                  {column.sortable ? (
                    <button type="button" className="column-sort" onClick={() => props.onSort(column.key)}>
                      {column.label}
                    </button>
                  ) : (
                    column.label
                  )}
                  {column.filter}
                </span>
                <button
                  type="button"
                  className="column-resize"
                  aria-label={`Resize ${column.label}`}
                  onPointerDown={(event) => startResize(column.key, event)}
                  onPointerMove={moveResize}
                  onPointerUp={endResize}
                  onKeyDown={(event) => stepResize(column, event)}
                />
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <tr key={props.rowKey(row)} className={props.rowClassName?.(row)}>
            {props.columns.map((column) =>
              column.rowHeader ? (
                <th key={column.key} scope="row" className={column.className}>
                  {column.cell(row)}
                </th>
              ) : (
                <td key={column.key} className={column.className}>
                  {column.cell(row)}
                </td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
